import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { motion, AnimatePresence, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { createPremiumJob, detectFaces } from '../api/endpoints';
import { isCreditError } from '../api/config';
import { useUser } from '../contexts/UserContext';
import { navigateTo } from '../utils/navigation';
import { getCroppedImageBlob } from '../utils/cropImage';
import CameraCapture from '../components/CameraCapture';
import PhotoConsentGate from '../components/PhotoConsentGate';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';
import { useCreditGate } from '../hooks/useCreditGate';

const FEATURE_KEY = 'premium_face';

export type PremiumFaceScanInput = Record<string, never>;

export interface PremiumFaceScanResult {
  jobId: string;
}

const PremiumFaceScanScreen: React.FC<ScreenProps<PremiumFaceScanInput, PremiumFaceScanResult>> = ({
  mode,
  onComplete,
  onCancel,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [faceStatus, setFaceStatus] = useState<string>('');
  const [checking, setChecking] = useState<boolean>(false);
  const [hasCamera, setHasCamera] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [photoConsentGranted, setPhotoConsentGranted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkCamera = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setHasCamera(devices.some((d) => d.kind === 'videoinput'));
      } catch {
        setHasCamera(false);
      }
    };
    void checkCamera();
  }, []);

  const { credits, costFor, refreshCredits } = useUser();
  // Credits gate the send, never the photo or the face check (SCRUM-196).
  const gate = useCreditGate(FEATURE_KEY);
  const cost = costFor(FEATURE_KEY);
  const total = credits ? gate.spendable : null;
  const outOfQuota = gate.block === 'insufficient';

  const fileInputRef = useRef<HTMLInputElement>(null);

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
  const quick: Transition = prefersReduced ? { duration: 0 } : { duration: 0.18 };

  const headerIn: Variants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: spring } };
  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 14, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: spring },
    exit: { opacity: 0, y: -8, transition: quick },
  };
  const innerStagger: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReduced ? 0 : 0.06, delayChildren: prefersReduced ? 0 : 0.05 } },
  };
  const innerItem: Variants = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: spring } };

  const helperText = useMemo(() => {
    if (step === 1) return 'Step 1 — Upload a photo for your Premium Skin Read.';
    if (checking) return 'Step 2 — Reviewing your photo…';
    if (faceStatus === 'Good') return 'Perfect! Ready for your premium skin read.';
    if (faceStatus.startsWith('Bad')) return 'Center your face in the frame.';
    return 'Step 2 — Center your face in the square for best results.';
  }, [step, checking, faceStatus]);

  const handleBlob = (blob: Blob) => {
    setImageSrc(URL.createObjectURL(blob));
    setFaceStatus('');
    setError(null);
    setStep(2);
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  useEffect(() => {
    if (!imageSrc || !croppedAreaPixels) return;
    const timer = setTimeout(() => {
      void (async () => {
        setChecking(true);
        try {
          const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, { size: 768 });
          const res = await detectFaces(blob);
          setFaceStatus(res.response);
        } catch {
          setFaceStatus('Error detecting face');
        } finally {
          setChecking(false);
        }
      })();
    }, 1000);
    return () => { clearTimeout(timer); };
  }, [imageSrc, croppedAreaPixels]);

  const handleUploadJob = async () => {
    if (faceStatus !== 'Good' || !imageSrc || !croppedAreaPixels) return;
    if (!(await gate.ensure())) return;
    setUploading(true);
    setError(null);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, { size: 768 });
      const res = await createPremiumJob(blob);
      void refreshCredits();
      if (mode === 'modal') {
        setUploading(false);
        onComplete?.({ jobId: res.job_id });
      } else {
        navigateTo(`/premium-analysis/${res.job_id}/pending`);
      }
    } catch (error_: unknown) {
      if (isCreditError(error_)) {
        setError(error_.message);
        void refreshCredits();
      } else {
        setError(getErrorMessage(error_, 'Failed to upload. Please try again.'));
      }
      setUploading(false);
    }
  };

  const openPicker = () => fileInputRef.current?.click();
  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleBlob(file);
    e.currentTarget.value = '';
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files.item(0);
    if (file === null) return;
    handleBlob(file);
  };

  const StatusChip = () => {
    if (!faceStatus && !checking) return null;
    if (checking) {
      return (
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={spring}
          className="pill"
          style={{ background: 'rgba(245,158,11,0.12)', color: '#92400e' }}
        >
          <span className="dot" style={{ background: '#f59e0b' }} />
          Reviewing…
        </motion.div>
      );
    }
    const isGood = faceStatus === 'Good';
    return (
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={spring}
        className={isGood ? 'pill pill--teal' : 'pill'}
        style={isGood ? undefined : { color: 'var(--c-error)' }}
      >
        <span className={`dot${isGood ? '' : ' dot--coral'}`} />
        {faceStatus}
      </motion.div>
    );
  };

  const PlusIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );

  const ScannerFrame = () => (
    <div className="absolute inset-0 pointer-events-none z-10">
      <div className="absolute top-4 left-4 w-8 h-8 border-t-[1.5px] border-l-[1.5px] rounded-tl-lg" style={{ borderColor: 'var(--c-kivo-teal)' }} />
      <div className="absolute top-4 right-4 w-8 h-8 border-t-[1.5px] border-r-[1.5px] rounded-tr-lg" style={{ borderColor: 'var(--c-kivo-teal)' }} />
      <div className="absolute bottom-4 left-4 w-8 h-8 border-b-[1.5px] border-l-[1.5px] rounded-bl-lg" style={{ borderColor: 'var(--c-kivo-teal)' }} />
      <div className="absolute bottom-4 right-4 w-8 h-8 border-b-[1.5px] border-r-[1.5px] rounded-br-lg" style={{ borderColor: 'var(--c-kivo-teal)' }} />
      {checking && (
        <motion.div
          initial={{ top: '10%' }}
          animate={{ top: '90%' }}
          transition={{ duration: 2.2, repeat: Infinity, ease: [0, 0, 1, 1], repeatType: 'loop' }}
          className="absolute left-4 right-4 h-px"
          style={{
            background: 'linear-gradient(to right, transparent, var(--c-kivo-teal), transparent)',
            boxShadow: '0 0 20px rgba(42,164,166,0.9), 0 0 40px rgba(42,164,166,0.3)',
          }}
        />
      )}
    </div>
  );

  const handleConsentAccepted = useCallback(() => {
    setPhotoConsentGranted(true);
  }, []);

  const inner = (
    <>
      <PhotoConsentGate onAccepted={handleConsentAccepted} />
      {gate.sheet}

      {photoConsentGranted && (
        <div className="page-guard w-full max-w-[100svw] overflow-x-hidden stack-5">
          <style>{`@supports not (width: 1svw) { .page-guard { max-width: 100vw; } }`}</style>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onPicked}
            className="hidden"
            aria-hidden="true"
          />

          <motion.div
            variants={headerIn}
            initial="hidden"
            animate="visible"
            className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4"
          >
            <div className="text-center sm:text-left flex-1">
              {mode === 'page' && (
                <div className="row-3 justify-center sm:justify-start mb-2">
                  <h1 className="t-h2">Premium Skin Read</h1>
                </div>
              )}
              <div className="min-h-[36px] flex items-center justify-center sm:justify-start">
                <p className="t-small t-mute max-w-sm">{helperText}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center justify-center sm:justify-end">
              <AnimatePresence>{(step === 2 || checking) && <StatusChip />}</AnimatePresence>

              {cost !== null && (
                <span
                  className={outOfQuota ? 'pill' : 'pill pill--teal'}
                  style={outOfQuota ? { color: 'var(--c-error)' } : undefined}
                >
                  {outOfQuota ? (
                    <><span className="dot dot--coral" />Need {cost} credits</>
                  ) : (
                    <><span className="dot" />{cost} credits{total !== null ? ` · ${total} left` : ''}</>
                  )}
                </span>
              )}
              {gate.note}
              {gate.unlockedLine}
            </div>
          </motion.div>

          <AnimatePresence mode="wait" initial={false}>
            {step === 1 && (
              <motion.section
                key="step1"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="relative group"
              >
                {/* Subtle teal glow on hover */}
                <div
                  className="absolute -inset-0.5 rounded-3xl blur opacity-0 group-hover:opacity-100 transition duration-700 group-hover:duration-200 pointer-events-none"
                  style={{ background: 'linear-gradient(135deg, var(--c-teal-weak), rgba(42,164,166,0.08))' }}
                />
                <div className="card card--bordered relative flex flex-col items-center" style={{ borderRadius: 'var(--r-xl)', padding: 'var(--s-7) var(--s-6)' }}>
                  <motion.div variants={innerStagger} initial="hidden" animate="visible" className="w-full max-w-2xl stack-6">

                    <motion.div variants={innerItem} className="t-small t-mute text-center">
                      Photos are analyzed for cosmetic, appearance-based insights only. This service does not provide medical advice or diagnosis.
                    </motion.div>

                    {/* Drop zone */}
                    <motion.div
                      variants={innerItem}
                      onClick={openPicker}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={onDrop}
                      className="drop-zone"
                    >
                      <>
                          <motion.div
                            className="relative mb-6"
                            animate={prefersReduced ? {} : { y: [0, -6, 0] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', repeatType: 'loop' }}
                          >
                            {/* Pulsing glow ring */}
                            <motion.div
                              className="absolute -inset-4 pointer-events-none"
                              animate={prefersReduced ? {} : { scale: [1, 1.2, 1], opacity: [0.35, 0.08, 0.35] }}
                              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                              style={{ borderRadius: 'var(--r-lg)', background: 'var(--c-teal-weak)', filter: 'blur(14px)' }}
                            />
                            <div className="drop-zone__icon drop-zone__icon--teal relative" style={{ width: 72, height: 72, borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-lifted)' }}>
                              <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8" aria-hidden="true">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                            </div>
                            <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 flex items-center justify-center bg-white" style={{ borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-soft)', color: 'var(--c-kivo-teal)' }}>
                              <PlusIcon className="w-4 h-4" />
                            </div>
                          </motion.div>
                          <h3 className="t-h3 mb-1">Upload Your Portrait</h3>
                          <p className="t-small t-mute mb-2">Drag image file or click to browse</p>
                          <span className="t-label" style={{ color: 'var(--c-kivo-teal)' }}>768px Resolution · AI Ready</span>
                          <div className="hidden sm:flex gap-2 mt-4">
                            <span className="pill">Portrait</span>
                            <span className="pill">Clear Lighting</span>
                          </div>
                        </>
                    </motion.div>

                    {hasCamera && (
                      <motion.div variants={innerItem} className="stack-4 items-center">
                        <div className="w-full flex items-center gap-4 t-label" style={{ color: 'var(--ink-muted)' }}>
                          <div className="flex-1 h-px" style={{ background: 'var(--rule)' }} />
                          or use live camera
                          <div className="flex-1 h-px" style={{ background: 'var(--rule)' }} />
                        </div>
                        <div
                          className="w-full max-w-sm overflow-hidden bg-black"
                          style={{ borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lifted)' }}
                        >
                          <CameraCapture onCapture={handleBlob} />
                        </div>
                      </motion.div>
                    )}

                    <AnimatePresence>
                      {error && (
                        <motion.p
                          key={error}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2 }}
                          className="t-small"
                          style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                        >
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              </motion.section>
            )}

            {step === 2 && imageSrc && (
              <motion.section
                key="step2"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="flex flex-col items-center"
              >
                <style>{`
                  .reactEasyCrop_CropArea { border: 1px solid rgba(255,255,255,0.3) !important; border-radius: 8px; box-shadow: 0 0 0 9999px rgba(0,0,0,0.85) !important; }
                  .reactEasyCrop_Container { background: #0a0a0a; }
                `}</style>

                <div className="w-full max-w-[640px]">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={spring}
                    className="relative w-full aspect-square sm:aspect-video overflow-hidden bg-black"
                    style={{ borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-deep)' }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="rect"
                      showGrid={true}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      zoomWithScroll
                      minZoom={1}
                      maxZoom={6}
                      restrictPosition
                    />
                    <ScannerFrame />

                    <div className="absolute inset-x-4 bottom-4 flex items-end justify-between pointer-events-none z-20 gap-2">
                      <div className="stack-2">
                        <span className="hud-badge hud-badge--teal">Preview {zoom.toFixed(1)}x</span>
                        <span className="hud-badge hud-badge--dim">768 × 768px</span>
                      </div>
                      <div
                        className="w-10 h-10 flex items-center justify-center mb-1"
                        style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)', borderRadius: 'var(--r-sm)', border: '1px solid rgba(255,255,255,0.1)' }}
                      >
                        <div className="w-5 h-5 border-2 border-white/20 rounded-sm relative">
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--c-kivo-teal)' }} />
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={spring}
                    className="mt-6 stack-4 items-center"
                  >
                    {/* Zoom slider */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                      className="row-4 w-full max-w-[480px] px-5 py-3"
                      style={{ background: 'var(--c-cream)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-soft)', border: '1px solid var(--rule)' }}
                    >
                      <span className="t-label" style={{ color: 'var(--ink-muted)', flexShrink: 0 }}>Zoom</span>
                      <div className="flex-1">
                        <input
                          type="range"
                          min={1}
                          max={6}
                          step={0.01}
                          value={zoom}
                          onChange={(e) => { setZoom(Number.parseFloat(e.target.value)); }}
                          className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                          style={{ accentColor: 'var(--c-kivo-teal)', background: 'var(--rule)' }}
                        />
                      </div>
                      <span className="t-small" style={{ color: 'var(--ink)', fontWeight: 600, minWidth: 32 }}>{zoom.toFixed(1)}x</span>
                    </motion.div>

                    <AnimatePresence>
                      {error && (
                        <motion.p
                          key={error}
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.2 }}
                          className="t-small w-full max-w-[480px]"
                          style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                        >
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <div style={{ display: 'flex', gap: 12 }}>
                      <motion.button
                        whileHover={prefersReduced ? {} : { y: -1 }}
                        whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                        onClick={() => { setStep(1); setImageSrc(null); setFaceStatus(''); setError(null); }}
                        className="btn btn--ghost btn--sm"
                        style={{ padding: '12px 24px' }}
                      >
                        Abort
                      </motion.button>
                      <motion.button
                        whileHover={prefersReduced ? {} : { y: -1 }}
                        whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                        onClick={handleUploadJob}
                        disabled={faceStatus !== 'Good' || uploading}
                        className="btn btn--primary"
                        style={{ padding: '12px 32px', minWidth: 180 }}
                      >
                        {uploading ? (
                          <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(248,244,238,0.3)', borderTopColor: 'var(--c-cream)' }} />
                        ) : (
                          'Initialize Analysis'
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );

  if (mode === 'modal') {
    return (
      <ModalShell
        title="Premium face scan"
        onClose={() => onCancel?.()}
        widthClass="max-w-3xl"
      >
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default PremiumFaceScanScreen;
